import React, { useState } from 'react';
import { Alert, Box, Button, ButtonBase, CircularProgress, IconButton, LinearProgress, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { ActivityField, DescriptionField, EffortField, EffortTypeField, ProjectField, TicketField } from './fields';
import { formatMinutes, parseEffort } from '../../utils/effortParser';
import { DAILY_WARNING_MINUTES } from '../../utils/worklogRules';
import { segmentedSx } from '../monthly/MultiSelectFilter';
import { cardSx, cardTitleSx, GREEN, GREEN_DARK, GREEN_SOFT, INK, MUTED } from '../home/homeTheme';

// Con pochi tipi di effort la scelta è a bottoni, altrimenti un menu
const MAX_SEGMENTED_TYPES = 4;
const QUICK_MINUTES = [30, 60, 120, 240];
const sectionSx = { fontSize: 12, fontWeight: 700, color: MUTED, mb: 0.75 };
// "Normal (NORM)" → "Normal": il nome completo resta nel tooltip
const shortTypeName = (name) => String(name || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || String(name || '').trim();

/**
 * Form di inserimento come nella pagina classica: progetto, attività e tipo in comune,
 * una o più righe con ticket, effort (minuti) e descrizione, un solo salvataggio.
 */
export default function EntryCard({
  common,
  rows,
  errors,
  onCommonChange,
  onRowChange,
  onAddRow,
  onRemoveRow,
  onPaste,
  onSave,
  saving,
  projects,
  activities,
  effortTypes,
  project,
  activity,
  prefix,
  blockMessage,
  savedMinutes,
  pendingMinutes,
  dayLabel,
}) {
  const [activeKey, setActiveKey] = useState(null);
  const total = savedMinutes + pendingMinutes;
  const over = total > DAILY_WARNING_MINUTES;
  const readyRows = rows.filter((r) => parseEffort(r.effort).valid).length;

  // Le scorciatoie valgono per la riga su cui si sta lavorando (l'ultima, se nessuna ha il focus)
  const activeRow = rows.find((r) => r.key === activeKey) || rows[rows.length - 1];
  const activeMinutes = parseEffort(activeRow?.effort).minutes || 0;
  const restOfDay = Math.max(0, DAILY_WARNING_MINUTES - savedMinutes - (pendingMinutes - activeMinutes));
  const setEffort = (minutes) => activeRow && onRowChange(activeRow.key, { effort: String(minutes) });

  return (
    <Box
      sx={{ ...cardSx, height: 'auto', p: 0, overflow: 'hidden' }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          onSave();
        }
      }}
    >
      <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.75 }}>
          <Typography sx={cardTitleSx}>Add worklog</Typography>
          {dayLabel && <Typography sx={{ fontSize: 12.5, color: MUTED }}>{dayLabel}</Typography>}
        </Box>

        <Typography sx={sectionSx}>What you worked on</Typography>
        <Box sx={{ display: 'grid', gap: 1, alignItems: 'start', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1.6fr) minmax(0,1.4fr) auto' }, mb: 2 }}>
          <ProjectField pill projects={projects} value={common.projectId} onChange={(v) => onCommonChange({ projectId: v })} error={errors.common.project} />
          <ActivityField pill activities={activities} value={common.activityId} onChange={(v) => onCommonChange({ activityId: v })} error={errors.common.activity} />
          {effortTypes.length && effortTypes.length <= MAX_SEGMENTED_TYPES ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={common.effortTypeId}
              onChange={(_, v) => v != null && onCommonChange({ effortTypeId: v })}
              sx={{ ...segmentedSx, '& .MuiToggleButton-root': { ...segmentedSx['& .MuiToggleButton-root'], height: 40 } }}
              aria-label="Effort type"
            >
              {effortTypes.map((et) => (
                <Tooltip key={et.id} title={et.name.trim()}>
                  <ToggleButton value={et.id}>{shortTypeName(et.name)}</ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          ) : (
            <Box sx={{ minWidth: 180 }}>
              <EffortTypeField pill effortTypes={effortTypes} value={common.effortTypeId} onChange={(v) => onCommonChange({ effortTypeId: v })} error={errors.common.effortTypeId} />
            </Box>
          )}
        </Box>

        {blockMessage && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {blockMessage}
          </Alert>
        )}

        <Typography sx={sectionSx}>Entries</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row) => {
            const e = errors.rows[row.key] || {};
            const focus = () => setActiveKey(row.key);
            return (
              <Box
                key={row.key}
                sx={{ display: 'grid', gap: 1, alignItems: 'start', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1.2fr) 130px minmax(0,3fr) 36px' } }}
              >
                <TicketField
                  pill
                  value={row.ticket}
                  onChange={(v) => onRowChange(row.key, { ticket: v })}
                  onFocus={focus}
                  prefix={prefix}
                  required={!!activity?.requires_ticket_id}
                  hint={activity?.requires_ticket_id ? project?.regex_desc : ''}
                  error={e.ticket}
                  onPaste={(ev) => onPaste(row.key, 'ticket', ev)}
                />
                <EffortField pill value={row.effort} onChange={(v) => onRowChange(row.key, { effort: v })} onFocus={focus} error={e.effort} onPaste={(ev) => onPaste(row.key, 'effort', ev)} />
                <DescriptionField
                  pill
                  value={row.description}
                  onChange={(v) => onRowChange(row.key, { description: v })}
                  onFocus={focus}
                  required={!!activity?.requires_description}
                  error={e.description}
                  onPaste={(ev) => onPaste(row.key, 'description', ev)}
                />
                <Tooltip title="Remove entry">
                  <span>
                    <IconButton size="small" onClick={() => onRemoveRow(row.key)} disabled={rows.length === 1} sx={{ mt: 0.5 }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>

        {/* Scorciatoie per l'effort della riga attiva, in minuti */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
          <Typography sx={{ fontSize: 12, color: MUTED, mr: 0.25 }}>Quick effort{rows.length > 1 ? ` · entry ${rows.indexOf(activeRow) + 1}` : ''}</Typography>
          {QUICK_MINUTES.map((m) => (
            <ButtonBase key={m} onClick={() => setEffort(m)} sx={chipSx(activeMinutes === m)}>
              {m} min
            </ButtonBase>
          ))}
          {restOfDay > 0 && (
            <ButtonBase onClick={() => setEffort(restOfDay)} sx={{ ...chipSx(activeMinutes === restOfDay), borderColor: GREEN, color: GREEN_DARK }}>
              Rest of day · {restOfDay} min
            </ButtonBase>
          )}
        </Box>

        {errors.general && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {errors.general}
          </Alert>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, px: { xs: 2, md: 2.5 }, py: 1.5, bgcolor: 'var(--rt-subtle)', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button color="inherit" startIcon={<AddIcon />} onClick={onAddRow} sx={{ textTransform: 'none', color: MUTED, fontWeight: 600 }}>
          Add entry
        </Button>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              <Box component="span" sx={{ fontWeight: 700, color: over ? 'var(--rt-warn)' : INK }}>
                {formatMinutes(savedMinutes)} saved
              </Box>
              {pendingMinutes > 0 && ` + ${formatMinutes(pendingMinutes)} new`}
            </Typography>
            <Typography sx={{ fontSize: 13, color: over ? 'var(--rt-warn)' : MUTED }}>
              {over ? `${formatMinutes(total - DAILY_WARNING_MINUTES)} over 8h` : `${formatMinutes(DAILY_WARNING_MINUTES - total)} left`}
            </Typography>
          </Box>
          <LinearProgress
            variant="buffer"
            value={Math.min(100, (savedMinutes / DAILY_WARNING_MINUTES) * 100)}
            valueBuffer={Math.min(100, (total / DAILY_WARNING_MINUTES) * 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'var(--rt-track)',
              '& .MuiLinearProgress-bar1Buffer': { bgcolor: over ? '#EF7A1A' : GREEN, borderRadius: 4 },
              '& .MuiLinearProgress-bar2Buffer': { bgcolor: over ? '#F7C29A' : '#BFE0AE', borderRadius: 4 },
              '& .MuiLinearProgress-dashed': { display: 'none' },
            }}
          />
        </Box>
        <Tooltip title="Ctrl / ⌘ + Enter">
          <span>
            <Button
              variant="contained"
              color="success"
              onClick={onSave}
              disabled={saving}
              sx={{ minWidth: 150, textTransform: 'none', fontWeight: 700, borderRadius: 2.5, boxShadow: 'none', bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK, boxShadow: 'none' } }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : readyRows > 1 ? `Save ${readyRows} worklogs` : 'Save worklog'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

function chipSx(active) {
  return {
    height: 28,
    px: 1.25,
    borderRadius: 999,
    border: '1px solid',
    borderColor: active ? GREEN : 'var(--rt-pill-border)',
    bgcolor: active ? GREEN_SOFT : 'var(--rt-paper)',
    color: active ? GREEN_DARK : INK,
    fontSize: 12.5,
    fontWeight: active ? 700 : 500,
    '&:hover': { borderColor: GREEN },
  };
}
